import { getNextUnit, getVisibleSections, getPreviousUnit } from 'utils/htmlBookUtils'
import { CONTENT_RESTRICTION_RATIO } from 'utils/constants/constants'
import { isDesktop } from 'utils/rebrandingUtils'

export const isUnitFirstPage = (pages, currentPageId) => {
  if (!pages || !currentPageId) return false
  const firstPage = pages[0]
  return firstPage && firstPage.id === currentPageId
}

export const isUnitLastPage = (pages, currentPageId) => {
  if (!pages || !currentPageId) return false
  const lastPage = pages[pages.length - 1]
  return lastPage && lastPage.id === currentPageId
}

export const getCoverImageFromUnit = (unit) => {
  if (!unit) return null
  return unit.thumbnails?.mini?.url
}

export const getInitialPageId = (initialSectionId, unitPaperPages, sections) => {
  if (!unitPaperPages?.length || !initialSectionId) return null

  const firstPageId = sections.find((section) => section.id === initialSectionId)?.firstPageId
  if (firstPageId) {
    return firstPageId
  }

  const initialSectionPage = unitPaperPages.find((page) => page.sectionId === initialSectionId)
  if (initialSectionPage) {
    return initialSectionPage.id
  }

  return null
}

export const groupPagesByPosition = (pages) => {
  const pairs = []
  let currentPair = {}

  pages.forEach((page) => {
    if (page.position === 'LEFT') {
      currentPair = { left: page }
    } else if (page.position === 'RIGHT') {
      currentPair.right = page
      pairs.push(currentPair)
      currentPair = {}
    }
  })

  if (currentPair.left && !currentPair.right) {
    pairs.push(currentPair)
  }

  return pairs
}

const getClosestVisibleSectionFromLearningPhase = (page, pageSection, sectionsInLearningPhase) => {
  const hasVisibleSectionInLearningPhase = sectionsInLearningPhase.some(
    (section) => section.isVisible
  )

  if (hasVisibleSectionInLearningPhase) {
    const pageSectionIndexInLearningPhase = sectionsInLearningPhase.findIndex(
      (section) => section.id === pageSection.id
    )
    for (let i = pageSectionIndexInLearningPhase - 1; i >= 0; i--) {
      const section = sectionsInLearningPhase[i]
      if (section.isVisible) {
        return {
          ...page,
          sectionId: section.id,
        }
      }
    }

    for (let i = pageSectionIndexInLearningPhase + 1; i < sectionsInLearningPhase.length; i++) {
      const section = sectionsInLearningPhase[i]
      if (section.isVisible) {
        return {
          ...page,
          sectionId: section.id,
        }
      }
    }
  }
  return null
}

export const getPaperPagesWithRightHost = (unitPaperPages, hostContentServer) => {
  return unitPaperPages.map((page) => ({
    ...page,
    resolutions: {
      ...page.resolutions,
      large: {
        ...page.resolutions.large,
        path: `${hostContentServer}/${page.resolutions.large.path}`,
      },
      thumbnail: {
        ...page.resolutions.thumbnail,
        path: `${hostContentServer}/${page.resolutions.thumbnail.path}`,
      },
    },
  }))
}

// If a page's section is not visible, it will try to find the closest visible section
// within the same learning phase, or in the previous or next learning phases.
export const getPaperPagesWithRightVisibleSection = (unitPaperPages, unitPaperSections) => {
  const allLearningPhases = unitPaperSections.reduce((uniquePhases, section) => {
    const phase = section.learningPhase
    if (phase?.id && !uniquePhases.some((p) => p.id === phase.id)) {
      uniquePhases.push(phase)
    }
    return uniquePhases
  }, [])

  return unitPaperPages
    .map((page) => {
      // We assume that each section has at least one page
      const pageSection = unitPaperSections.find((section) => section.id === page.sectionId)

      if (pageSection && pageSection.isVisible) {
        return page
      }

      const learningPhaseId = pageSection?.learningPhase?.id
      const sectionsInLearningPhase = unitPaperSections.filter(
        (section) => section.learningPhase?.id === learningPhaseId
      )
      const closestVisibleFromLearningPhase = getClosestVisibleSectionFromLearningPhase(
        page,
        pageSection,
        sectionsInLearningPhase
      )
      if (closestVisibleFromLearningPhase) {
        return closestVisibleFromLearningPhase
      }

      const currentLearningPhaseIndex = allLearningPhases.findIndex(
        (lp) => lp.id === learningPhaseId
      )
      for (let i = currentLearningPhaseIndex - 1; i >= 0; i--) {
        const prevLearningPhaseId = allLearningPhases[i].id
        const visibleSectionsInPrevPhase = unitPaperSections.filter(
          (s) => s.learningPhase.id === prevLearningPhaseId && s.isVisible
        )
        if (visibleSectionsInPrevPhase.length > 0) {
          return {
            ...page,
            sectionId: visibleSectionsInPrevPhase[visibleSectionsInPrevPhase.length - 1].id,
          }
        }
      }

      for (let i = currentLearningPhaseIndex + 1; i < allLearningPhases.length; i++) {
        const nextLearningPhaseId = allLearningPhases[i].id
        const visibleSectionsInNextPage = unitPaperSections.filter(
          (s) => s.learningPhase.id === nextLearningPhaseId && s.isVisible
        )
        if (visibleSectionsInNextPage.length > 0) {
          return {
            ...page,
            sectionId: visibleSectionsInNextPage[0].id,
          }
        }
      }

      return null
    })
    .filter(Boolean)
}

export const getPaperViewRightNavTarget = (
  unitPaperPages,
  currentVisiblePages,
  bookStructure,
  currentUnitId
) => {
  const isLastPage = isUnitLastPage(
    unitPaperPages,
    currentVisiblePages[currentVisiblePages.length - 1]?.id
  )

  const nextUnit = getNextUnit(bookStructure, currentUnitId)
  const hasNextUnit = !!nextUnit
  const showRightNavItem = hasNextUnit || !isLastPage

  if (!showRightNavItem) return null

  if (isLastPage && hasNextUnit) {
    const nextUnitSections = getVisibleSections(nextUnit)
    const targetSectionId = nextUnitSections?.[0]?.id

    return {
      hasNextUnit,
      isLastPage,
      kind: 'unit',
      nextUnit,
      targetSectionId,
    }
  }

  const lastVisibleId = currentVisiblePages?.[currentVisiblePages.length - 1]?.id
  const indexOfLastVisible = unitPaperPages.findIndex((p) => p.id === lastVisibleId)
  if (indexOfLastVisible < 0) {
    return {
      hasNextUnit,
      isLastPage,
      kind: 'page',
      nextUnit,
      targetSectionId: null,
    }
  }

  const nextPageIndex = indexOfLastVisible + 1
  const nextPage = unitPaperPages[nextPageIndex]
  const targetSectionId = nextPage?.sectionId

  return {
    hasNextUnit,
    isLastPage,
    kind: 'page',
    nextUnit,
    targetSectionId,
  }
}

export const getPaperViewLeftNavTarget = (
  unitPaperPages,
  currentVisiblePages,
  bookStructure,
  currentUnitId,
  windowWidth
) => {
  const isFirstPage = isUnitFirstPage(unitPaperPages, currentVisiblePages[0]?.id)
  const previousUnit = getPreviousUnit(bookStructure, currentUnitId)
  const hasPreviousUnit = !!previousUnit
  const showLeftNavItem = hasPreviousUnit || !isFirstPage

  if (!showLeftNavItem) return null

  if (isFirstPage && hasPreviousUnit) {
    const prevUnitSections = getVisibleSections(previousUnit)
    const targetSectionId = prevUnitSections?.[prevUnitSections.length - 1]?.id

    return {
      hasPreviousUnit,
      isFirstPage,
      kind: 'unit',
      previousUnit,
      targetSectionId,
    }
  }
  const layoutIsDouble = isDesktop(windowWidth)

  const firstVisibleId = currentVisiblePages?.[0]?.id
  const indexOfFirstVisible = unitPaperPages.findIndex((p) => p.id === firstVisibleId)
  if (indexOfFirstVisible < 0) {
    return {
      hasPreviousUnit,
      isFirstPage,
      kind: 'page',
      previousUnit,
      targetSectionId: null,
    }
  }

  let previousPageIndex
  if (!layoutIsDouble) {
    previousPageIndex = indexOfFirstVisible - 1
  } else {
    previousPageIndex = indexOfFirstVisible - 2
    if (previousPageIndex < 0) {
      previousPageIndex = indexOfFirstVisible - 1
    }
  }
  const previousPage = unitPaperPages[previousPageIndex]
  const targetSectionId = previousPage?.sectionId

  return {
    hasPreviousUnit,
    isFirstPage,
    kind: 'page',
    previousUnit,
    targetSectionId,
  }
}

export const getLimitedPagesCount = (unitPaperPages) => {
  return Math.ceil(unitPaperPages.length * CONTENT_RESTRICTION_RATIO)
}
